import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { ENDED_LISTING_STATES } from './shared/constants';
import { LISTING_STATES } from '@reverbdotcom/commons/src/constants';
import URLHelpers from './shared/url_helpers';
import { withUserContext, IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import AddToCartButton, { AddToCartButtonFragment } from '@reverbdotcom/commons/src/components/add_to_cart_button';
import { useEppoAssignment, useUser } from '@reverbdotcom/commons/src/user_hooks';
import { EppoExperimentValues } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';
import Item2ListingState from './item2_listing_state';
import { Item2Stats, Item2StatsFragment } from './item2_stats';
import CreateOfferButton, { canAcceptOffers, ListingCreateOfferButtonFragment } from '@reverbdotcom/commons/src/offers/create_offer_button';
import PriceWithShipping, { Item2PriceWithShippingFragment } from '@reverbdotcom/commons/src/components/price_with_shipping';
import { ListingQuantitySelector } from '@reverbdotcom/commons/src/components/ListingQuantitySelector';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import CompareOtherSellerLink, { CompareOtherSellersLinkFragment } from './compare_other_sellers_link';
import { Item2WhyToBuySignals } from './item2_nudge_signals';
import { Item2EndedListingSearchButton } from './item2_ended_listing_search_button';
import {
  core_apimessages_CartItem as CartItem,
  core_apimessages_Offer_OfferStatus as OfferStatus,
  Item2PricingNegotiationsDataFragment,
  Item2PricingDataFragment,
  Item2AvailableActionsDataFragment,
  CreateUserActionMeDataFragment,
  BumpKeyFragment,
  arbiter_Interaction_InteractionType,
  SignalsDataFragment,
  core_apimessages_ProtectionPlan,
} from '@reverbdotcom/commons/src/gql/graphql';
import { BuyItNowButton, BuyItNowButtonFragment } from './BuyItNowButton';
import { ListingFinancingCallout, ListingFinancingCalloutFragment } from './ListingFinancingCallout';
import { DisplayStyle, WatchBadge } from '@reverbdotcom/commons/src/components/watch_badge';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';
import { gql } from '@reverbdotcom/commons/src/gql';
import { isDigital } from '@reverbdotcom/commons/src/listing_helpers';
import DownloadButton, { DigitalDetailsFragment } from '../digital_listings/download_button';
import { useBumpInteractionTracking } from '@reverbdotcom/commons/src/trackBumpInteraction';
import { ItemPageProtectionPlanUpperAddOn } from './ItemPageProtectionPlanUpperAddOn';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { isEU } from '@reverbdotcom/commons/src/geo';

type Negotiation = Item2PricingNegotiationsDataFragment;
type Listing = Item2PricingDataFragment & SignalsDataFragment;

interface IProps {
  listing: Listing & BumpKeyFragment;
  negotiations?: Negotiation[];
  cartItems?: CartItem[];
  isRolledUpFeaturedListingExpEnabled?: boolean;
  onFeaturedListing?: boolean;
  watchPrompt?: boolean;
  showKlarnaCallout?: boolean;
  availableActions?: Item2AvailableActionsDataFragment[];
  me?: CreateUserActionMeDataFragment;
  parentComponentName?: string;
  availableProtectionPlans?: core_apimessages_ProtectionPlan[];
  setProtectionPlanId?: (protectionPlanId) => void;
  protectionPlanId?: string;
  loading?: boolean;
}

export const Item2PricingFragment = gql(`
  fragment Item2PricingData on Listing {
    _id
    id
    offersEnabled
    inventory
    hasInventory
    maxBuyerQuantity
    make
    model
    state
    protectionPlanEligible
    condition {
      conditionSlug
    }
    shop {
      _id
      address {
        _id
        countryCode
        country {
          _id
          name
        }
      }
    }
    ...AddToCartButtonFields
    ...Item2PriceWithShippingData
    ...CompareOtherSellersLinkData
    ...ListingFinancingCalloutFields
    ...Item2StatsData
    ...BuyItNowButtonFields
    ...ListingCreateOfferButtonData
    ...DigitalDetailsFields
  }
  ${AddToCartButtonFragment}
  ${Item2PriceWithShippingFragment}
  ${CompareOtherSellersLinkFragment}
  ${ListingFinancingCalloutFragment}
  ${Item2StatsFragment}
  ${BuyItNowButtonFragment}
  ${ListingCreateOfferButtonFragment}
  ${DigitalDetailsFragment}
`);

export const Item2PricingNegotiationsFragment = gql(`
  fragment Item2PricingNegotiationsData on Negotiation {
    _id
    id
    lastOffer {
      status
    }
  }
`);

export const PROTECTION_PLAN_OPTIONS_QUERY = gql(`
  query Core_ListingProtectionPlanOptions(
    $listingId: String
  ) {
    listing(
      input: {
        id: $listingId
      }
    ) {
      _id
      protectionPlanOptions {
        availablePlans {
          planId
          durationMonths
          title
          amount {
            amount
            display
          }
          termsUrl
        }
      }
    }
  }
`);

const PRICE_RECORDS_QUERY = gql(`
  query Core_Listing_Layout_Price_Record(
    $listingId: String
  ) {
    priceRecordsSearch(input: {
      listingId: $listingId,
    }) {
      priceRecords {
        _id
        amountProduct {
          display
        }
      }
    }
  }
`);

function usePriceRecordsQuery(listing: Listing, user: Partial<IUser>) {
  const isSoldListing = [LISTING_STATES.ORDERED, LISTING_STATES.SOLD_OUT].includes(listing.state);
  const isEuOrUkUser = isEU(user.countryCode) || user.countryCode === 'GB';

  return useQuery(
    PRICE_RECORDS_QUERY,
    {
      skip: !isSoldListing || !isEuOrUkUser,
      variables: {
        listingId: listing.id,
      },
    },
  );
}

const ACTIVE_OFFER_STATUSES = [
  OfferStatus.ACTIVE,
  OfferStatus.COUNTERED,
];

const INACTIVE_OFFER_STATUSES = [
  OfferStatus.ACCEPTED,
  OfferStatus.REJECTED,
  OfferStatus.EXPIRED,
];

export function isLive(listing: Item2PricingDataFragment): boolean {
  return listing.state === LISTING_STATES.LIVE;
}

function isEndedListing(listing: Item2PricingDataFragment): boolean {
  return Object.values(ENDED_LISTING_STATES).includes(listing.state);
}

function isBrandNew(listing: Item2PricingDataFragment): boolean {
  return listing.condition.conditionSlug === 'brand-new';
}

function showActiveOffer({ listing, negotiations }: { listing: Listing, negotiations: Negotiation[] }) {
  return listing.offersEnabled
    && !isEmpty(negotiations)
    && !!negotiations.find(n => ACTIVE_OFFER_STATUSES.includes(n.lastOffer.status));
}

function showOfferHistory({ listing, negotiations }: { listing: Listing, negotiations: Negotiation[] }) {
  return listing.offersEnabled
    && !isEmpty(negotiations)
    && !!negotiations.find(n => INACTIVE_OFFER_STATUSES.includes(n.lastOffer.status));
}

function showMakeOffer({ listing, negotiations }: { listing: Listing, negotiations: Negotiation[] }) {
  if (listing.offersEnabled && isEmpty(negotiations)) {
    return true;
  }

  return showOfferHistory({ listing, negotiations });
}

function offerUrl(negotiation: Negotiation): string {
  return URLHelpers.offerPath({
    offerId: negotiation.id,
    status: 'all',
  });
}

function listingOffersPath(listing: Item2PricingDataFragment): string {
  return URLHelpers.listingOffersPath({
    product_id: listing.id,
    status: 'all',
  });
}

function endedListingStatusIndicatorText(listing: Item2PricingDataFragment) {
  switch (listing.state) {
    case LISTING_STATES.ENDED:
      return I18n.t('discovery.item2Pricing.statusIndicatorEnded');
    case LISTING_STATES.ORDERED:
    case LISTING_STATES.SOLD_OUT:
      return I18n.t('discovery.item2Pricing.statusIndicatorSold');
    case LISTING_STATES.SELLER_UNAVAILABLE:
      return I18n.t('discovery.item2Pricing.statusIndicatorUnavailable');
    default:
      return null;
  }
}

function ListingPricingActionsButtonRefresh({
  listing,
  negotiations,
  watchPrompt,
  availableActions,
  me,
  parentComponentName,
  availableProtectionPlans,
  setProtectionPlanId,
  protectionPlanId,
  showBuyItNowButton,
  showFreeDownloadButton,
  quantity,
  AddToCartButtonComponent,
  watchButtonText,
}) {
  const pricingButtonLayout = useEppoAssignment({ flagKey: eppoFlags.ITEM_PAGE_BUTTON_REFRESH });

  const shouldUseIconButtonStyle =
    pricingButtonLayout === 'treatment_2' &&
    canAcceptOffers(listing) &&
    (showMakeOffer({ listing, negotiations }) || showActiveOffer({ listing, negotiations }));

  const watchBadgeDisplayStyle = shouldUseIconButtonStyle ? DisplayStyle.ICON_BUTTON : DisplayStyle.ANIMATION_BUTTON;

  const pricingActionsClassName = () => {
    switch (pricingButtonLayout) {
      case 'treatment_1':
        return 'item2-pricing__actions--treatment-1';
      case 'treatment_2':
        return 'item2-pricing__actions--treatment-2';
      default:
        return 'item2-pricing__actions';
    }
  };

  return (
    <div className={pricingActionsClassName()}>
      {isLive(listing) &&
        <>
          {showBuyItNowButton &&
            <BuyItNowButton
              listing={listing}
              quantity={quantity}
              protectionPlanId={protectionPlanId}
            />
          }
          {showFreeDownloadButton &&
            <DownloadButton variants={listing.digitalDetails.variants} isFree />
          }
          {!showFreeDownloadButton &&
            <div>
              <AddToCartButtonComponent
                listing={listing}
                quantity={quantity}
                disabled={!isLive}
                parentComponentName={parentComponentName}
                protectionPlanId={protectionPlanId}
                availableProtectionPlans={availableProtectionPlans}
                setProtectionPlanId={setProtectionPlanId}
              />
            </div>
          }
          <Item2ListingState state={listing.state} />
          {showActiveOffer({ listing, negotiations }) &&
            <div className="margin-1">
              <a
                data-view-offer
                className="button width-100"
                href={offerUrl(negotiations[0])}
              >
                {I18n.t('discovery.item2Pricing.viewActiveOffer')}
              </a>
            </div>
          }
          {showMakeOffer({ listing, negotiations }) &&
            <CreateOfferButton
              listing={listing}
              disabled={isEndedListing(listing)}
              availableActions={availableActions}
              me={me}
              parentComponentName={parentComponentName}
            />
          }
          <div className="item2-pricing__watch">
            <WatchBadge
              listing={listing}
              displayStyle={watchBadgeDisplayStyle}
              parentComponentName={parentComponentName}
              autoWatch={isLive(listing) && !!watchPrompt}
              watchText={watchButtonText.watchText}
              unwatchText={watchButtonText.unwatchText}
              watchTooltipText={watchButtonText.watchTooltipText}
              unwatchTooltipText={watchButtonText.unwatchTooltipText}
              useToast
            />
          </div>
        </>
      }
      <CompareOtherSellerLink listing={listing} />
    </div>
  );
}

function useWatchButtonText(isLiveListing: boolean) {
  const watchButtonAssignment = useEppoAssignment({ flagKey: eppoFlags.WATCH_BUTTON_TEXT_ON_ITEM_PAGE });

  let watchText, unwatchText, watchTooltipText, unwatchTooltipText;

  switch (watchButtonAssignment) {
    case `${EppoExperimentValues.Treatment}_1`:
      watchText = I18n.t('commons.watchBadge.save');
      unwatchText = I18n.t('commons.watchBadge.saved');
      watchTooltipText = I18n.t('commons.watchBadge.saveTooltip');
      unwatchTooltipText = I18n.t('commons.watchBadge.removeTooltip');
      break;
    case `${EppoExperimentValues.Treatment}_2`:
      watchText = I18n.t('commons.watchBadge.saveToFavorites');
      unwatchText = I18n.t('commons.watchBadge.savedToFavorites');
      watchTooltipText = I18n.t('commons.watchBadge.saveTooltip');
      unwatchTooltipText = I18n.t('commons.watchBadge.removeTooltip');
      break;
    default:
      watchText = isLiveListing ? I18n.t('commons.watchBadge.watch') : I18n.t('commons.watchBadge.watchEndedListing');
      unwatchText = I18n.t('commons.watchBadge.unwatch');
      watchTooltipText = I18n.t('commons.watchBadge.watchTooltip');
      unwatchTooltipText = I18n.t('commons.watchBadge.unwatchTooltip');
      break;
  }

  return {
    watchText,
    unwatchText,
    watchTooltipText,
    unwatchTooltipText,
  };
}

export function Item2Pricing({
  listing,
  negotiations = [],
  isRolledUpFeaturedListingExpEnabled = false,
  onFeaturedListing = false,
  cartItems = [],
  watchPrompt = false,
  showKlarnaCallout = false,
  availableActions = [],
  me = undefined,
  parentComponentName = WatchParentComponentNames.ItemPageActions,
  availableProtectionPlans = [],
  setProtectionPlanId = () => { },
  protectionPlanId = null,
  loading = false,
}: IProps) {
  const [quantity, setQuantity] = React.useState(1);
  const bumpInteractionTracking = useBumpInteractionTracking({
    interactionType: arbiter_Interaction_InteractionType.CLICK,
    bumpKey: listing?.bumpKey,
    listingId: listing?.id,
  });

  const user = useUser();

  const showProtectionPlanAddOn = isLive(listing) && !onFeaturedListing && listing.protectionPlanEligible;

  const setQuantityAndTrackBump = (quantity) => {
    setQuantity(quantity);
    bumpInteractionTracking(`${parentComponentName}:listingQuantitySelector`);
  };

  const watchButtonText = useWatchButtonText(isLive(listing));

  const isFree = listing.pricing?.buyerPrice?.amountCents === 0;

  const isListingPreorder = !!listing.preorderInfo && listing.preorderInfo.onPreorder;

  const showBuyItNowButton = (!onFeaturedListing || isRolledUpFeaturedListingExpEnabled) && !isListingPreorder && !isDigital(listing);

  const showWatchBadge = !isRolledUpFeaturedListingExpEnabled;

  const showLoudPreorderButton = !onFeaturedListing && isListingPreorder && !isDigital(listing);

  const showFreeDownloadButton = isDigital(listing) && isFree && !!listing.digitalDetails?.variants;

  const priceRecordsResult = usePriceRecordsQuery(listing, user);
  const isPriceRecordLoading = priceRecordsResult.loading;
  const soldPrice = priceRecordsResult?.data?.priceRecordsSearch?.priceRecords?.[0]?.amountProduct?.display;

  const classes = classNames(
    'item2-pricing',
    'item2-pricing--signal-system',
    { 'item2-pricing--buy-it-now': showBuyItNowButton },
    { 'item2-pricing--loud-preorder': showLoudPreorderButton },
  );

  if (!listing) {
    return null;
  }

  return (
    <div className={classes}>
      <div className="item2-pricing__amount">
        {!isPriceRecordLoading &&
          <PriceWithShipping
            listing={listing}
            cartItems={cartItems}
            onFeaturedListing={onFeaturedListing}
            inlineRibbon
            showCombinedRate
            soldPrice={soldPrice}
          />
        }
      </div>
      {isLive(listing) && (
        <ListingFinancingCallout
          listing={listing}
          showKlarnaCallout={showKlarnaCallout}
        />
      )}
      {isLive(listing) &&
        <Item2WhyToBuySignals
          listing={listing}
          onFeaturedListing={onFeaturedListing}
        />
      }
      {listing.hasInventory && isLive(listing) && isBrandNew(listing) && !isDigital(listing) &&
        <div className="item2-pricing__quantity">
          <ListingQuantitySelector
            onQuantityChange={setQuantityAndTrackBump}
            totalInStock={listing.inventory}
            quantity={quantity}
            maxBuyerQuantity={listing.maxBuyerQuantity}
            price={listing.pricing?.buyerPrice}
            onPreorder={isListingPreorder}
          />
        </div>
      }
      {showProtectionPlanAddOn &&
        <div className="mt-3">
          <ItemPageProtectionPlanUpperAddOn
            listing={listing}
            availableProtectionPlans={availableProtectionPlans}
            setProtectionPlanId={setProtectionPlanId}
            protectionPlanId={protectionPlanId}
            disabled={quantity > 1}
            loading={loading}
          />
        </div>
      }
      {!onFeaturedListing ?
        <ListingPricingActionsButtonRefresh
          listing={listing}
          negotiations={negotiations}
          watchPrompt={watchPrompt}
          availableActions={availableActions}
          me={me}
          availableProtectionPlans={availableProtectionPlans}
          setProtectionPlanId={setProtectionPlanId}
          protectionPlanId={protectionPlanId}
          showBuyItNowButton={showBuyItNowButton}
          showFreeDownloadButton={showFreeDownloadButton}
          quantity={quantity}
          AddToCartButtonComponent={AddToCartButton}
          parentComponentName={parentComponentName}
          watchButtonText={watchButtonText}
        />
        :
        <div className="item2-pricing__actions">
          {isLive(listing) &&
            <>
              {showBuyItNowButton &&
                <BuyItNowButton
                  listing={listing}
                  quantity={quantity}
                  protectionPlanId={protectionPlanId}
                />
              }
              {showFreeDownloadButton &&
                <DownloadButton variants={listing.digitalDetails.variants} isFree />
              }
              {!showFreeDownloadButton &&
                <div>
                  <AddToCartButton
                    listing={listing}
                    quantity={quantity}
                    disabled={!isLive}
                    parentComponentName={parentComponentName}
                    protectionPlanId={protectionPlanId}
                    availableProtectionPlans={availableProtectionPlans}
                    setProtectionPlanId={setProtectionPlanId}
                  />
                </div>
              }
              <Item2ListingState state={listing.state} />
              {showActiveOffer({ listing, negotiations }) &&
                <div className="margin-1">
                  <a
                    data-view-offer
                    className="button width-100"
                    href={offerUrl(negotiations[0])}
                  >
                    {I18n.t('discovery.item2Pricing.viewActiveOffer')}
                  </a>
                </div>
              }
              {showMakeOffer({ listing, negotiations }) &&
                <CreateOfferButton
                  listing={listing}
                  disabled={isEndedListing(listing)}
                  availableActions={availableActions}
                  me={me}
                  parentComponentName={parentComponentName}
                />
              }
              {showWatchBadge &&
                <div className="item2-pricing__watch">
                  <WatchBadge
                    listing={listing}
                    displayStyle={DisplayStyle.ANIMATION_BUTTON}
                    parentComponentName={parentComponentName}
                    autoWatch={isLive(listing) && !!watchPrompt}
                    watchText={watchButtonText.watchText}
                    unwatchText={watchButtonText.unwatchText}
                    watchTooltipText={watchButtonText.watchTooltipText}
                    unwatchTooltipText={watchButtonText.unwatchTooltipText}
                    useToast
                  />
                </div>
              }
            </>
          }
          <CompareOtherSellerLink listing={listing} />
        </div>
      }
      {showOfferHistory({ listing, negotiations }) &&
        <div className="size-90 scaling-mt-4 align-center">
          <RCAlertBox type="plain">
            <SanitizedRender
              data-view-offer-history
              html={
                I18n.t('discovery.item2Pricing.viewOfferHistory', {
                  linkOpen: `<a href=${listingOffersPath(listing)}>`,
                  linkClose: '</a>',
                })
              }
            />
          </RCAlertBox>
        </div>
      }
      {/* Ended Listing Status Indicator Section */}
      {!isLive(listing) &&
        <div className="item2-pricing__status-indicator-container">
          <div className="item2-sold-text">
            <p className="item2-sold-text__title">
              {endedListingStatusIndicatorText(listing)}
            </p>
            <p className="item2-sold-text__subtitle">
              {I18n.t('discovery.item2Pricing.statusIndicatorViewSimilar')}
            </p>
          </div>
          <Item2EndedListingSearchButton
            make={listing.make}
            model={listing.model}
            listingId={listing.id}
          />
          <div className="item2-pricing__watch">
            <WatchBadge
              listing={listing}
              displayStyle={DisplayStyle.ANIMATION_BUTTON}
              parentComponentName={parentComponentName}
              autoWatch={isLive(listing) && !!watchPrompt}
              watchText={watchButtonText.watchText}
              unwatchText={watchButtonText.unwatchText}
              watchTooltipText={watchButtonText.watchTooltipText}
              unwatchTooltipText={watchButtonText.unwatchTooltipText}
              useToast
            />
          </div>
        </div>
      }
      {!onFeaturedListing &&
        <Item2Stats
          listing={listing}
        />
      }
    </div>
  );
}

export default withUserContext(Item2Pricing);
